// Shared Workforce Status widget — the "who's where" tracker (collapsible).
// Used on the main dashboard, CEO, Recruiting, and HR dashboards.
import { useState } from 'react';
import Icon from './ui/Icon';
import { useQuery } from '@tanstack/react-query';
import { candidatesApi } from '../lib/api';

// Workforce Status — the "who's where" tracker the CEO watches (and can send to
// clients): counts per status category + a day/night split + pending starts.
// Collapsible so it doesn't dominate the page.
export default function WorkforceStatusWidget() {
  const [open, setOpen] = useState(true);
  const { data } = useQuery({ queryKey: ['workforce-status'], queryFn: () => candidatesApi.workforceStatus() });
  const d = data?.data;
  const CATS: Array<{ key: keyof NonNullable<typeof d>['categories']; label: string; color: string }> = [
    { key: 'submitted_pending', label: 'Submitted to client — pending', color: '#a855f7' },
    { key: 'pending_bg', label: 'Pending background', color: '#f59e0b' },
    { key: 'cleared_bg_pending_start', label: 'Cleared BG — pending start', color: 'var(--info)' },
    { key: 'start_scheduled', label: 'Start date scheduled', color: '#10b981' },
    { key: 'actively_working', label: 'Actively working', color: '#059669' },
    { key: 'withdrew_declined', label: 'Withdrew / declined', color: 'var(--danger)' },
    { key: 'in_pipeline', label: 'In pipeline (other)', color: 'var(--text-muted)' },
  ];
  return (
    <div className="pn" style={{ marginTop: 20 }}>
      <div className="pnh" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setOpen((v) => !v)}>
        <h3><Icon name="target" size={16} /> Workforce Status {d ? <span style={{ fontSize: 12, color: 'var(--t3)', fontWeight: 400 }}>· {d.total} active</span> : null}</h3>
        <span style={{ fontSize: 13, color: 'var(--t3)', display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ display: 'inline-flex', transform: open ? undefined : 'rotate(-90deg)', transition: 'transform var(--dur) var(--ease-out)' }}><Icon name="chevron-down" size={11} /></span> {open ? 'Hide' : 'Show'}</span>
      </div>
      {open && (
        <div className="pnb">
          {!d ? <div style={{ color: 'var(--t3)', fontSize: 13 }}>Loading…</div> : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                {CATS.map((c) => (
                  <div key={c.key} style={{ background: 'var(--sf3)', border: '1px solid var(--bd)', borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: c.color }}>{d.categories[c.key]}</div>
                    <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>{c.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 12, display: 'flex', gap: 16, fontSize: 13, color: 'var(--t2)', flexWrap: 'wrap' }}>
                <span><Icon name="sun" size={14} /> Day: <strong>{d.shifts.day}</strong></span>
                <span><Icon name="moon" size={14} /> Night: <strong>{d.shifts.night}</strong></span>
                <span><Icon name="refresh" size={14} /> Both: <strong>{d.shifts.both}</strong></span>
                {d.shifts.unspecified > 0 && <span style={{ color: 'var(--t3)' }}>Unspecified: {d.shifts.unspecified}</span>}
              </div>
              {d.pending_starts.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Pending / scheduled start dates</div>
                  <div style={{ display: 'grid', gap: 4 }}>
                    {d.pending_starts.slice(0, 12).map((p) => (
                      <div key={p.id} style={{ display: 'flex', gap: 10, fontSize: 13, alignItems: 'center' }}>
                        <span style={{ fontWeight: 600, minWidth: 160 }}>{p.name}</span>
                        <span style={{ color: 'var(--t3)' }}>{p.role ?? '—'}</span>
                        <span style={{ fontSize: 11, color: p.shift === 'night' ? 'var(--ai)' : 'var(--info-fg)' }}>{p.shift}</span>
                        <span style={{ marginLeft: 'auto', color: 'var(--t3)' }}>{p.start_date ?? 'TBD'} · {p.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

